import React from 'react'
import { convertFieldsToGraphqlVariables } from '../../graphql-helpers';
import { get } from 'immutadot';
import { parse } from 'date-fns';
import * as XLSX from 'xlsx';
import { AreaBooking, AreaBookingItem, Campaign, PosterSize, RegionType, useCampaignsQueryQuery } from "../../generated/graphql";
import { useToast } from "../../contexts/toast";
import { useLocation } from "react-router";
import QueryString from "query-string";
import { FieldTableColumnProps } from "../../components/FieldTable/FieldTableTypes";
import { ReportColumn } from "../report-column";

function dateToExcel(inDate: Date) {
    const returnDateTime = 25569.0 + ((inDate.getTime() - (inDate.getTimezoneOffset() * 60 * 1000)) / (1000 * 60 * 60 * 24));
    return returnDateTime.toString().substr(0, 5);
}
type QSP = {
    name: string,
    type: string,
    value: any
};

// flatten the Area Booking Item, adding the campaign and totals
type ABICampaign = {
    areaBookingItems?: any[] | null,
    campaign?: any,
    AreaBookingItemRegion?: string | null,
    TotalValue?: number | null,
    posterSize?: any | null,
    regionType?: any | null,
    revenue?: number | null,
    currency?: any | null
}

type XlsCell = { t: string, v: any, f: any, w: undefined | number, z: undefined | number | string };

export const AreaBookingReport = ({ params, done }: { params?: any, done: () => void }) => {
    const location = useLocation();
    const rawParsedQueryString = QueryString.parse(location.search);
    const queryStringArray: QSP[] = [];
    Object.entries(rawParsedQueryString).forEach(function ([k, v]) {
        const name = k.split('-');
        queryStringArray.push({ name: name[0], type: name[1], value: v });
    });
    const gqlParams = convertFieldsToGraphqlVariables(queryStringArray);
    gqlParams.with_areaBooking = true;
    //const toastId = 'cost-report';
    const { toast, dismiss } = useToast();
    toast('Generating report', { id: 'GEN_REPORT', timeout: 0 });
    const currency = { glyph: '£' };
    const query = useCampaignsQueryQuery({
        //useQuery(campaignsQuery,{
        variables: gqlParams,
        fetchPolicy: "network-only",
        onCompleted: (v) => {
            console.log('done', v);
            toast('Rendering report', { id: 'GEN_REPORT' });
            const pixPerLetter = 7;
            const rawValues = v.campaigns?.data || [];
            console.log("rawValues", rawValues)
            const tableValues: any[] = [];
            rawValues.forEach(row => {
                if (!row.areaBookings?.length) return;
                row.areaBookings.forEach((ab) => {
                    ab?.areaBookingItems?.forEach(item => {
                        const c: ABICampaign = { ...ab };
                        c.campaign = row;
                        c.AreaBookingItemRegion = item?.campaignComponent?.region?.name;
                        c.TotalValue = (item?.campaignComponent?.qty ?? 0) * (item?.campaignComponent?.unit_value ?? 0);
                        c.currency = item?.campaignComponent?.currency;
                        // Calculate revenue here
                        const discountAmount = (c.campaign.discountableTotal) * (c.campaign.discount_percentage / 100);
                        const discountAmountGoodWill = c.campaign.discount_final;
                        let discountedTotalCampaign;
                        if (discountAmount !== null && discountAmountGoodWill !== null && discountAmount > 0 && discountAmountGoodWill > 0) {
                            discountedTotalCampaign = (c.campaign.discountableTotal) - (discountAmount + discountAmountGoodWill);
                        } else if (discountAmount !== null && discountAmount > 0) {
                            discountedTotalCampaign = (c.campaign.discountableTotal) - discountAmount;
                        } else if (discountAmountGoodWill !== null && discountAmountGoodWill > 0) {
                            discountedTotalCampaign = c.campaign.discountableTotal - discountAmountGoodWill;
                        } else {
                            discountedTotalCampaign = c.campaign.discountableTotal;
                        }
                        c.revenue = (discountedTotalCampaign / c.campaign.discountableTotal) * c.TotalValue;
                        tableValues.push(c);
                    });
                });
            });
            console.log('values is', tableValues);
            const eData = [];
            const columns: ReportColumn[] = [
                {
                    accessor: 'campaign.job_reference',
                    header: 'Job Ref',
                }, {
                    accessor: "campaign.status",
                    header: "Status",
                },
                {
                    accessor: "campaign.name",
                    header: "Job Name",
                },
                {
                    accessor: "campaign.clientCompany.sales_company",
                    header: "Client",
                },
                {
                    accessor: "campaign.salesTeam.name",
                    header: "Sales Team",
                },
                {
                    accessor: "campaign.ourContact.name",
                    header: "Account Manager",
                },
                {
                    accessor: "campaign.start_date",
                    header: "Start Date",
                    exportAs: 'dateformat'
                },
                {
                    accessor: "campaign.completion_date",
                    header: "Completion Date",
                    exportAs: 'dateformat'
                },
                {
                    accessor: "posterSize.name",
                    header: "Poster Format",
                },
                {
                    accessor: "posterSize.name",
                    header: "Format Type",
                    exportAs: 'posterformat'
                },
                {
                    accessor: "regionType.name",
                    header: "Region Type",
                },
                {
                    accessor: "AreaBookingItemRegion",
                    header: "Region Name",
                },
                {
                    accessor: "TotalValue",
                    header: "Revenue",
                    exportAs: 'currency',
                    options: {
                        currency: {
                            accessor: 'currency'
                        }
                    },
                },
                {
                    accessor: "campaign.totals.area_booking",
                    header: "Total Area Booking",
                    exportAs: 'currency',
                    options: {
                        currency: {
                            accessor: 'currency'
                        }
                    },
                },
                {
                    accessor: "revenue",
                    header: "Discounted Revenue",
                    exportAs: 'currency',
                    options: {
                        currency: {
                            accessor: 'currency'
                        }
                    }
                },
            ];
            const worksheet: any = { '!ref': 'A1:' };
            let col = 1;
            const colwidths: any[] = [];
            columns.forEach((column) => {
                const header = column.headerRaw ? column.headerRaw : column.header;
                worksheet[String.fromCharCode(64 + col) + 1] = { t: 's', v: header };
                colwidths.push({ wpx: header.toString().length });
                col++;
            });
            worksheet['!ref'] = 'A1:' + String.fromCharCode(64 + col) + '1';
            eData.push(columns.map((column) => column.header));
            let rowIdx = 2;
            let lastCell = '';
            tableValues.forEach((row) => {
                let col = 1;
                columns.forEach((column) => {
                    const cell: XlsCell = { t: 's', v: undefined, f: undefined, w: undefined, z: undefined };
                    let value: any = null;
                    value = get(row, column.sort ? column.sort : column.accessor);
                    const rawValue = value;
                    switch (column.exportAs) {
                        case 'percentage':
                            cell.t = 'n';
                            //                        cell.v = parseFloat(value?value.replace('%', ''):'');
                            cell.z = '#.##"%"';
                            cell.v = value;
                            if (typeof value == 'number') {
                                cell.v = value / 100;
                            }
                            if (typeof value == 'string') {
                                cell.v = parseFloat(value ? value.replace('%', '') : '');
                            }
                            if (isNaN(cell.v) || cell.v == null) {
                                cell.v = 0;
                            }
                            //cell.w = value; 
                            //value = parseFloat(value.replace('%', ''));
                            break;
                        case 'currency':
                            cell.t = 'n';
                            cell.v = value;
                            if (typeof value == 'number') {
                                cell.v = value / 100;
                            }
                            if (typeof value == 'string') {
                                cell.v = parseFloat(value ? value.replace(/[^0-9\\.]/, '') : '');
                            }
                            if (isNaN(cell.v) || cell.v === null) {
                                cell.v = 0;
                            }
                            console.log('cell is', cell,column.options);
                            let currency = undefined;
                            // use currency accessor
                            if(column.options?.currency?.accessor) {
                                currency = get(row, column.options.currency.accessor);
                            }
                            //                        if(!cell.v) { cell.v = value; }
                            if(currency?.glyph) {
                                cell.z = ('"' + (currency.glyph) + '"' + '#,##0.00');
                            } else {
                                // olde version
                                cell.z = '"£"' + '0.00';
                            }
                            break;
                        case 'boolean':
                            if (typeof value == 'object') {
                                cell.v = rawValue ? 'Y' : 'N';
                            } else {
                                cell.v = value ? 'Y' : 'N';
                            }
                            break;
                        case 'posterformat':
                            cell.v = value;
                            if (typeof cell.v === 'string' && cell.v.startsWith('UA')) {
                                cell.v = 'Un-authorised';
                            } else {
                                cell.v = 'Authorised';
                            }
                            break;
                        case 'dateformat':
                            cell.v = value;
                            const parts = value.split('-');
                            const formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
                            cell.v = formattedDate;
                            break;
                        default:
                            if (typeof value == 'object') {
                                if (value) {
                                    value = value.toString();
                                    cell.v = value;
                                } else {
                                    cell.v = ''; value = '';
                                }
                            }
                            else {
                                cell.v = value;
                            }
                            break;
                    }
                    if (cell.v) {
                        colwidths[col - 1].wpx = Math.max(colwidths[col - 1].wpx, cell.v.toString().length);
                    }
                    lastCell = String.fromCharCode(64 + col++) + rowIdx;
                    worksheet[lastCell] = cell;

                    //newRow.push(value);
                });
                rowIdx++;
                //            return newRow;
            });
            worksheet['!ref'] = 'A1:' + lastCell;
            colwidths.forEach(v => { v.wpx = v.wpx * pixPerLetter; });
            console.log('widths', colwidths);
            worksheet['!cols'] = colwidths;
            console.log('worksheet is', worksheet);
            //if (eData.length > 1) {
            //const ws = XLSX.utils.aoa_to_sheet(eData);
            //var ws = XLSX.utils.aoa_to_sheet(eData);
            const wb = XLSX.utils.book_new();
            //var cell = ws['A2'];
            //console.log(worksheets);
            //worksheet['A2'] = {t:'s',v: 'TEST'};
            XLSX.utils.book_append_sheet(wb, worksheet, "SheetJS");
            XLSX.writeFile(wb, "sheetjs.xlsx");
            //}
            done();
        },
        onError: () => {
            toast('Error generating report', { id: 'GEN_REPORT', variant: 'error' });
            done();
        }
    });
    console.log(query);
    return null;
}